// src/pages/MonthlyGemReport.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { supabase } from "../supabaseClient";
import { PERSONAS_DATA } from "@/data/personas_data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface JournalRow {
  id: string;
  created_at: string;
  ai_hawkins_level: number | null;
  ai_persona: string | null;
  ai_response: any | null;
}

interface DailyEnergyPoint {
  date: string; // YYYY-MM-DD
  maxLevel: number | null;
}

interface PersonaStat {
  persona: string;
  count: number;
  percentage: number;
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function startOfLocalDay(input: Date): Date {
  const d = new Date(input);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatShortDate(input: number | Date): string {
  const d = typeof input === "number" ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// Hawkins energy scale levels (discrete) as shown in the reference chart.
// We space each adjacent level evenly on the Y axis (not linear 0..1000).
const HAWKINS_LEVELS = [
  0,
  20,
  30,
  50,
  75,
  100,
  125,
  150,
  175,
  200,
  250,
  310,
  350,
  400,
  500,
  540,
  600,
  700,
  1000,
] as const;

const HAWKINS_TICK_LEVELS = HAWKINS_LEVELS.slice(0, -1);

const HAWKINS_BANDS = (HAWKINS_LEVELS as readonly number[]).filter(
  (n) => n !== 0 && n !== 1000,
);

function snapToHawkinsBand(raw: number | null): number | null {
  if (raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  // Treat non-positive numbers as missing.
  if (n <= 0) return null;

  let best = HAWKINS_BANDS[0];
  let bestDist = Math.abs(n - best);
  for (let i = 1; i < HAWKINS_BANDS.length; i++) {
    const level = HAWKINS_BANDS[i];
    const dist = Math.abs(n - level);
    if (dist < bestDist) {
      best = level;
      bestDist = dist;
    }
  }
  return best;
}

type PersonaDataEntry = {
  id?: string;
  name?: string;
  englishName?: string;
};

const PERSONA_KEY_BY_ID = new Map<string, string>();
const PERSONA_KEY_BY_ENGLISH_NAME = new Map<string, string>();

for (const [personaKey, personaValue] of Object.entries(
  PERSONAS_DATA as Record<string, PersonaDataEntry>,
)) {
  const id = typeof personaValue?.id === "string" ? personaValue.id.trim() : "";
  if (id) PERSONA_KEY_BY_ID.set(id.toLowerCase(), personaKey);

  const en =
    typeof personaValue?.englishName === "string"
      ? personaValue.englishName.trim()
      : "";
  if (en) PERSONA_KEY_BY_ENGLISH_NAME.set(en.toLowerCase(), personaKey);
}

function canonicalizePersonaKey(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";

  if ((PERSONAS_DATA as any)[trimmed]) return trimmed;

  const lowered = trimmed.toLowerCase();

  const byId = PERSONA_KEY_BY_ID.get(lowered);
  if (byId) return byId;

  const byEnglishName = PERSONA_KEY_BY_ENGLISH_NAME.get(lowered);
  if (byEnglishName) return byEnglishName;

  return trimmed;
}

function isZhLike(lang: string | undefined): boolean {
  const l = (lang || "").toLowerCase();
  return l === "zh" || l.startsWith("zh-");
}

function getPersonaDisplayName(personaKey: string, lang: string | undefined): string {
  const key = canonicalizePersonaKey(personaKey);
  const entry = (PERSONAS_DATA as any)[key] as PersonaDataEntry | undefined;
  if (!entry) return personaKey;

  if (isZhLike(lang)) return entry.name || key;
  return entry.englishName || entry.name || key;
}

function getPersonaEnergyLevelLabel(personaKey: string): string {
  const key = canonicalizePersonaKey(personaKey);
  const entry = (PERSONAS_DATA as any)[key] as
    | (PersonaDataEntry & { energy?: string })
    | undefined;
  const energy = typeof entry?.energy === "string" ? entry.energy.trim() : "";
  if (!energy) return "";

  // Stored as strings like: "75 · Grief" / "350/400 · Reason" / "700+ · Enlightenment"
  const numeric = energy.split("·")[0]?.trim() || "";
  return numeric;
}

function hawkinsIndex(level: number): number {
  const idx = (HAWKINS_LEVELS as readonly number[]).indexOf(level);
  return idx >= 0 ? idx : 0;
}

// Maps a raw Hawkins level into an evenly-spaced index axis where each adjacent
// pair in HAWKINS_LEVELS takes the same vertical distance.
function mapEnergyToBandY(raw: number): number {
  const value = clampNumber(raw, HAWKINS_LEVELS[0], HAWKINS_LEVELS[HAWKINS_LEVELS.length - 1]);

  for (let i = 0; i < HAWKINS_LEVELS.length - 1; i++) {
    const a = HAWKINS_LEVELS[i];
    const b = HAWKINS_LEVELS[i + 1];

    if (value <= b) {
      const t = b === a ? 0 : (value - a) / (b - a);
      return i + t;
    }
  }

  return HAWKINS_LEVELS.length - 1;
}

function deriveHawkinsLevelFromAiResponse(aiResponse: any | null): number | null {
  if (!aiResponse) return null;

  const meta = (aiResponse as any)?.meta;
  const raw =
    (meta && typeof meta === "object" ? (meta as any).hawkins_bucket : null) ??
    (meta && typeof meta === "object" ? (meta as any).primary_emotion : null) ??
    null;

  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  const m = s.match(/\d+/);
  if (m) {
    const n = parseInt(m[0], 10);
    return Number.isFinite(n) ? n : null;
  }

  const lowered = s.toLowerCase();
  const compact = lowered.replace(/\s+/g, "");
  const kwMap: Record<string, number> = {
    shame: 20,
    guilt: 30,
    apathy: 50,
    grief: 75,
    sadness: 75,
    fear: 100,
    anxiety: 100,
    desire: 125,
    craving: 125,
    anger: 150,
    rage: 150,
    pride: 175,
    courage: 200,
    willingness: 310,
    acceptance: 350,
    reason: 350,
    understanding: 350,
    love: 500,
    joy: 540,
    peace: 600,
    enlightenment: 700,
    oneness: 700,
  };
  if (kwMap[compact] != null) return kwMap[compact];

  // Chinese hints
  const zh = s.replace(/\s+/g, "");
  if (zh.includes("羞愧")) return 20;
  if (zh.includes("罪恶") || zh.includes("内疚")) return 30;
  if (zh.includes("冷漠") || zh.includes("麻木")) return 50;
  if (zh.includes("悲伤") || zh.includes("悲痛")) return 75;
  if (zh.includes("恐惧")) return 100;
  if (zh.includes("渴望") || zh.includes("欲望")) return 125;
  if (zh.includes("愤怒")) return 150;
  if (zh.includes("骄傲") || zh.includes("面子")) return 175;
  if (zh.includes("勇气")) return 200;
  if (zh.includes("意愿")) return 310;
  if (zh.includes("接受") || zh.includes("接纳") || zh.includes("理性") || zh.includes("理解")) return 350;
  if (zh.includes("爱")) return 500;
  if (zh.includes("喜悦")) return 540;
  if (zh.includes("平和")) return 600;
  if (zh.includes("开悟") || zh.includes("合一")) return 700;

  return null;
}

function derivePersonaFromHawkinsLevel(level: number | null): string | null {
  if (level == null) return null;
  if (level === 20) return "隐形人";
  if (level === 30) return "罪人";
  if (level === 50) return "旁观者";
  if (level === 75) return "受害者";
  if (level === 100) return "懦夫";
  if (level === 125) return "瘾君子";
  if (level === 150) return "暴君";
  if (level === 175) return "演员";
  if (level === 200) return "探险家";
  if (level === 250 || level === 310) return "实干家";
  if (level === 350 || level === 400) return "智者";
  if (level === 500) return "疗愈者";
  if (level === 540) return "庆祝者";
  if (level === 600) return "和平的观察者";
  if (level === 700) return "开悟者";
  return null;
}

export default function MonthlyGemReport() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const resolvedLang = i18n.resolvedLanguage || i18n.language;

  const reportExportRef = useRef<HTMLDivElement | null>(null);
  const [isSharePdfLoading, setIsSharePdfLoading] = useState(false);

  const [rows, setRows] = useState<JournalRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [monthlyAiInsight, setMonthlyAiInsight] = useState<string | null>(null);
  const [isMonthlyAiInsightLoading, setIsMonthlyAiInsightLoading] =
    useState(false);
  const [monthlyAiInsightError, setMonthlyAiInsightError] = useState<
    string | null
  >(null);

  const handleSharePdf = async () => {
    if (isSharePdfLoading) return;

    const el = reportExportRef.current;
    if (!el) return;

    flushSync(() => {
      setIsSharePdfLoading(true);
    });
    try {
      // Give React a moment to re-render export-only layout tweaks
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const bg = getComputedStyle(document.body).backgroundColor;

      const exportWidth = Math.max(el.scrollWidth, el.clientWidth);
      const exportHeight = Math.max(el.scrollHeight, el.clientHeight);

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: bg || undefined,
        windowWidth: exportWidth,
        windowHeight: exportHeight,
        width: exportWidth,
        height: exportHeight,
        scrollX: 0,
        scrollY: -window.scrollY,
        logging: false,
        ignoreElements: (node) => {
          if (!(node instanceof HTMLElement)) return false;
          return node.dataset.exportIgnore === "true";
        },
      });

      // Slice the rendered canvas into A4 pages to avoid page-bottom clipping.
      const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
      const pageWidthPt = pdf.internal.pageSize.getWidth();
      const pageHeightPt = pdf.internal.pageSize.getHeight();

      // Scale canvas pixels to fit page width.
      const pxPerPt = canvas.width / pageWidthPt;
      const pageHeightPx = Math.floor(pageHeightPt * pxPerPt);
      const overlapPx = Math.max(0, Math.floor(16 * pxPerPt));
      const stepPx = Math.max(1, pageHeightPx - overlapPx);

      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvas.width;
      const sliceCtx = sliceCanvas.getContext("2d");
      if (!sliceCtx) throw new Error("Failed to create canvas context");

      let pageIndex = 0;
      for (let y = 0; y < canvas.height; y += stepPx) {
        const sliceHeightPx = Math.min(pageHeightPx, canvas.height - y);
        sliceCanvas.height = sliceHeightPx;
        sliceCtx.clearRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        sliceCtx.drawImage(
          canvas,
          0,
          y,
          canvas.width,
          sliceHeightPx,
          0,
          0,
          canvas.width,
          sliceHeightPx,
        );

        const imgData = sliceCanvas.toDataURL("image/png");
        const imgHeightPt = sliceHeightPx / pxPerPt;

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pageWidthPt,
          imgHeightPt,
          undefined,
          "FAST",
        );
        pageIndex++;
      }

      const today = new Date().toISOString().slice(0, 10);
      pdf.save(`monthly-gem-report-${today}.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
    } finally {
      setIsSharePdfLoading(false);
    }
  };

  function buildMonthlyInsightSourceText(
    series: DailyEnergyPoint[],
    maxLevel: number,
    personaSummary: string,
  ): string {
    const sorted = [...series]
      .filter((p) => typeof p.date === "string")
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    const lines = sorted
      .slice(-30)
      .map((p) => `${p.date}: ${p.maxLevel ?? "null"}`)
      .join("\n");

    const numeric = sorted
      .map((p) => p.maxLevel)
      .filter((v): v is number => typeof v === "number");

    const first = numeric[0];
    const last = numeric[numeric.length - 1];
    const min = numeric.length ? Math.min(...numeric) : null;
    const max = numeric.length ? Math.max(...numeric) : null;
    const observedDays = sorted.length;
    const rangeStart = sorted[0]?.date || "";
    const rangeEnd = sorted[sorted.length - 1]?.date || "";

    return [
      "Monthly energy trend summary (Hawkins levels).",
      "",
      `Observed days (<= 30): ${observedDays}`,
      `Observed date range: ${rangeStart || "(unknown)"} to ${rangeEnd || "(unknown)"}`,
      `Energy stats: first=${typeof first === "number" ? first : "null"}, last=${typeof last === "number" ? last : "null"}, min=${typeof min === "number" ? min : "null"}, max=${typeof max === "number" ? max : (maxLevel || 0)}`,
      "",
      "Top personas distribution (approx %):",
      personaSummary || "(none)",
      "",
      "Daily max energy levels:",
      lines || "(no data)",
    ].join("\n");
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setErrorMsg(null);

      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          setErrorMsg(
            t("monthly.requireLogin", {
              defaultValue: "Please log in to view your monthly report.",
            }),
          );
          setIsLoading(false);
          return;
        }

        const now = new Date();
        const from = new Date(now);
        from.setDate(from.getDate() - 30);

        const { data, error } = await supabase
          .from("journals")
          .select("id, created_at, ai_hawkins_level, ai_persona, ai_response")
          .eq("user_id", user.id)
          .gte("created_at", from.toISOString())
          .order("created_at", { ascending: true });

        if (error) {
          console.error("Error loading monthly journals:", error);
          setErrorMsg(
            t("monthly.loadError", {
              defaultValue:
                "Something went wrong while loading your monthly gems.",
            }),
          );
          setIsLoading(false);
          return;
        }

        setRows((data as JournalRow[]) || []);
      } catch (err) {
        console.error("Unexpected error loading monthly report:", err);
        setErrorMsg(
          t("monthly.loadError", {
            defaultValue:
              "Something went wrong while loading your monthly gems.",
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const { energySeries, maxEnergyLevel, personaStats, insightText } = useMemo(() => {
    if (!rows || rows.length === 0) {
      return {
        energySeries: [] as DailyEnergyPoint[],
        maxEnergyLevel: 0,
        personaStats: [] as PersonaStat[],
        insightText: "",
      };
    }

    const energyMap = new Map<string, number>();
    const personaCounts = new Map<string, number>();
    let maxEnergyLevel = 0;

    for (const row of rows) {
      const dateKey = row.created_at.slice(0, 10); // YYYY-MM-DD

      const derivedLevel =
        row.ai_hawkins_level != null
          ? Number(row.ai_hawkins_level)
          : deriveHawkinsLevelFromAiResponse(row.ai_response);
      const current = snapToHawkinsBand(
        typeof derivedLevel === "number" && !Number.isNaN(derivedLevel)
          ? derivedLevel
          : null,
      );

      if (current != null) {
        const prev = energyMap.get(dateKey);
        if (prev == null || current > prev) {
          energyMap.set(dateKey, current);
        }
        if (current > maxEnergyLevel) {
          maxEnergyLevel = current;
        }
      }

      const personaRaw =
        row.ai_persona || derivePersonaFromHawkinsLevel(current);
      if (personaRaw && typeof personaRaw === "string") {
        const key = canonicalizePersonaKey(personaRaw);
        if (key) {
          personaCounts.set(key, (personaCounts.get(key) || 0) + 1);
        }
      }
    }

    const energySeries: DailyEnergyPoint[] = Array.from(energyMap.entries())
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([date, maxLevel]) => ({ date, maxLevel }));

    const totalPersona = Array.from(personaCounts.values()).reduce(
      (sum, v) => sum + v,
      0,
    );

    const personaStats: PersonaStat[] = totalPersona
      ? Array.from(personaCounts.entries())
          .map(([persona, count]) => ({
            persona,
            count,
            percentage: (count / totalPersona) * 100,
          }))
          .sort((a, b) => b.count - a.count)
      : [];

    let insightText = "";

    if (energySeries.length > 0) {
      const levels = energySeries
        .map((p) => p.maxLevel)
        .filter((v): v is number => typeof v === "number");

      if (levels.length > 0) {
        const avg = levels.reduce((s, v) => s + v, 0) / levels.length;
        const latest = levels[levels.length - 1];
        const topPersonaRaw = personaStats[0]?.persona || "";
        const topPersona = getPersonaDisplayName(topPersonaRaw, resolvedLang);

        insightText = t("monthly.insight.generic", {
          defaultValue:
            "In the past 30 days, your energy has hovered around {{avgLevel}} on the Hawkins scale, with {{topPersona}} visiting most often in your inner cast. Notice how this pattern feels in your body, and what tiny next step wants to emerge.",
          avgLevel: Math.round(avg),
          latestLevel: Math.round(latest),
          topPersona,
        });
      }
    }

    return { energySeries, maxEnergyLevel, personaStats, insightText };
  }, [rows, t, resolvedLang]);

  const handleGenerateMonthlyInsight = async () => {
    if (isMonthlyAiInsightLoading) return;
    if (!rows.length || energySeries.length === 0 || maxEnergyLevel <= 0) return;

    setIsMonthlyAiInsightLoading(true);
    setMonthlyAiInsightError(null);

    try {
      const personaSummary = personaStats
        .slice(0, 6)
        .map(
          (p) =>
            `${getPersonaDisplayName(p.persona, i18n.language)}: ${p.percentage.toFixed(0)}%`,
        )
        .join("\n");
      const journalText = buildMonthlyInsightSourceText(
        energySeries,
        maxEnergyLevel,
        personaSummary,
      );

      const { data, error } = await supabase.functions.invoke(
        "generate-ai-insight",
        {
          body: {
            journalId: null,
            journalText,
            mode: "spark",
            stage: "monthly",
            emotion: null,
            userLanguage: i18n.language,
          },
        },
      );

      if (error) throw error;

      const payload: any = data;
      const text =
        typeof payload?.insight === "string" ? payload.insight.trim() : null;

      if (text) {
        setMonthlyAiInsight(text);
      } else {
        setMonthlyAiInsight(null);
        setMonthlyAiInsightError(
          t("common.error", { defaultValue: "Something went wrong" }),
        );
      }
    } catch (e: any) {
      setMonthlyAiInsight(null);
      setMonthlyAiInsightError(
        e?.message ||
          t("common.error", { defaultValue: "Something went wrong" }),
      );
    } finally {
      setIsMonthlyAiInsightLoading(false);
    }
  };

  const { chartStartTs, chartEndTs, chartTicks } = useMemo(() => {
    const end = startOfLocalDay(new Date());
    const start = new Date(end);
    start.setDate(start.getDate() - 30);

    const startTs = start.getTime();
    const endTs = end.getTime();

    const ticks: number[] = [];
    for (let i = 0; i <= 30; i += 7) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      ticks.push(d.getTime());
    }
    if (ticks[ticks.length - 1] !== endTs) ticks.push(endTs);

    return {
      chartStartTs: startTs,
      chartEndTs: endTs,
      chartTicks: ticks,
    };
  }, []);

  const hasData = rows.length > 0;
  const canShare = !isLoading && !errorMsg && hasData && !isSharePdfLoading;

  return (
    <div
      className={
        "container mx-auto py-8 px-4 space-y-6 " +
        (isSharePdfLoading ? "max-w-none w-[896px]" : "max-w-4xl")
      }
    >
      <div ref={reportExportRef} className="space-y-6">
        <div className="mb-4 text-center">
          <h1 className="text-4xl font-bold text-primary mb-2">
            {t("monthly.title", { defaultValue: "Monthly Gem Report" })}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("monthly.subtitle", {
              defaultValue: "A 30-day view of your energy and emotions.",
            })}
          </p>
        </div>

        <div
          className="-mt-2 flex items-center justify-between"
          data-export-ignore="true"
        >
          <Button variant="default" size="sm" onClick={() => navigate("/my-gems")}>
            {t("common.back", { defaultValue: "Back" })}
          </Button>

          <Button
            variant="default"
            size="sm"
            onClick={handleSharePdf}
            disabled={!canShare}
          >
            {isSharePdfLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t("monthly.downloadPdf", { defaultValue: "Download as PDF" })}
          </Button>
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="py-12 flex items-center justify-center text-muted-foreground gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>
                {t("monthly.loading", {
                  defaultValue: "Loading your monthly report…",
                })}
              </span>
            </CardContent>
          </Card>
        ) : errorMsg ? (
          <Card>
            <CardContent className="py-12 text-center text-red-500 text-sm">
              {errorMsg}
            </CardContent>
          </Card>
        ) : !hasData ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground text-sm">
              {t("monthly.empty", {
                defaultValue:
                  "No gems found in the past 30 days. Capture a few new reflections to see your monthly trends.",
              })}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-primary">
                  {t("monthly.energyTitle", {
                    defaultValue: "Energy Level Over Time",
                  })}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2 pb-6">
                {energySeries.length === 0 || maxEnergyLevel <= 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("monthly.energyEmpty", {
                      defaultValue:
                        "No Hawkins energy data yet. Generate a few insights to see this chart.",
                    })}
                  </p>
                ) : (
                  <div
                    className={
                      "h-72 md:h-96 border rounded-lg " +
                      (isSharePdfLoading
                        ? "overflow-visible"
                        : "overflow-x-auto overflow-y-hidden")
                    }
                  >
                    <div
                      className={
                        "h-full px-6 py-4 " +
                        (isSharePdfLoading ? "min-w-0" : "min-w-[560px]")
                      }
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={energySeries
                            .map((p) => {
                              const ts = new Date(
                                p.date + "T00:00:00",
                              ).getTime();
                              const raw =
                                typeof p.maxLevel === "number"
                                  ? p.maxLevel
                                  : null;
                              return {
                                ts,
                                raw,
                                y:
                                  typeof raw === "number"
                                    ? mapEnergyToBandY(raw)
                                    : null,
                              };
                            })
                            .filter((p) => !Number.isNaN(p.ts))}
                          margin={{ top: 6, right: 12, left: 0, bottom: 8 }}
                        >
                          <CartesianGrid
                            stroke="hsl(var(--border))"
                            strokeDasharray="3 3"
                            horizontal={false}
                          />
                          <XAxis
                            dataKey="ts"
                            type="number"
                            scale="time"
                            domain={[chartStartTs, chartEndTs]}
                            ticks={chartTicks}
                            tickFormatter={(v) => formatShortDate(Number(v))}
                            tick={{
                              fontSize: 12,
                              fill: "hsl(var(--muted-foreground))",
                            }}
                            axisLine={{ stroke: "hsl(var(--border))" }}
                            tickLine={{ stroke: "hsl(var(--border))" }}
                            label={{
                              value: t("monthly.energyXAxis", {
                                defaultValue: "Date",
                              }),
                              position: "insideBottom",
                              offset: -4,
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 10,
                            }}
                          />
                          <YAxis
                            domain={[0, HAWKINS_LEVELS.length - 1]}
                            ticks={HAWKINS_TICK_LEVELS.map((_, idx) => idx)}
                            tickFormatter={(v) => {
                              const idx = Number(v);
                              if (!Number.isFinite(idx)) return "";
                              const rounded = Math.round(idx);
                              const level = HAWKINS_LEVELS[rounded];
                              if (level == null) return "";
                              if (level === 700) return "700-1000";
                              if (level === 1000) return "";
                              return String(level);
                            }}
                            allowDecimals={false}
                            tick={{
                              fontSize: 12,
                              fill: "hsl(var(--muted-foreground))",
                            }}
                            axisLine={{ stroke: "hsl(var(--border))" }}
                            tickLine={{ stroke: "hsl(var(--border))" }}
                            width={76}
                            label={{
                              value: t("monthly.energyYAxis", {
                                defaultValue: "Energy Level",
                              }),
                              angle: -90,
                              position: "insideLeft",
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 10,
                            }}
                          />

                          {HAWKINS_TICK_LEVELS.filter((v) => v !== 200).map(
                            (level) => (
                              <ReferenceLine
                                key={`lvl-${level}`}
                                y={hawkinsIndex(level)}
                                stroke="hsl(var(--primary))"
                                strokeDasharray="6 6"
                                strokeOpacity={0.22}
                              />
                            ),
                          )}

                          {/* Top boundary for the 700-1000 band */}
                          <ReferenceLine
                            y={hawkinsIndex(1000)}
                            stroke="hsl(var(--primary))"
                            strokeDasharray="6 6"
                            strokeOpacity={0.22}
                          />

                          {/* Keep the 200 reference line unchanged */}
                          <ReferenceLine
                            y={hawkinsIndex(200)}
                            stroke="hsl(var(--primary))"
                            strokeDasharray="6 6"
                            strokeOpacity={0.6}
                          />
                          <Tooltip
                            labelFormatter={(label) =>
                              formatShortDate(Number(label))
                            }
                            formatter={(v, _name, item) => {
                              const raw = (item && (item.payload as any)?.raw) as
                                | number
                                | null
                                | undefined;
                              const show = typeof raw === "number" ? raw : v;
                              return [
                                show,
                                t("monthly.energyYAxis", {
                                  defaultValue: "Energy",
                                }),
                              ];
                            }}
                            contentStyle={{
                              borderRadius: 8,
                              border: "1px solid hsl(var(--border))",
                              background: "hsl(var(--background))",
                              color: "hsl(var(--foreground))",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="y"
                            stroke="hsl(var(--primary))"
                            strokeWidth={3}
                            connectNulls
                            dot={{
                              r: 4,
                              stroke: "hsl(var(--primary))",
                              strokeWidth: 2,
                              fill: "hsl(var(--background))",
                            }}
                            activeDot={{
                              r: 5,
                              stroke: "hsl(var(--primary))",
                              strokeWidth: 2,
                              fill: "hsl(var(--background))",
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">
                  {t("monthly.emotionsTitle", {
                    defaultValue: "Top Personas This Month",
                  })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {personaStats.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("monthly.emotionsEmpty", {
                      defaultValue:
                        "We haven't picked up clear persona signals yet from your gems.",
                    })}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {personaStats.slice(0, 6).map((item) => (
                      <div
                        key={item.persona}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <span className="inline-block h-2 w-2 rounded-full bg-primary" />
                          <span>
                            {(() => {
                              const name = getPersonaDisplayName(
                                item.persona,
                                resolvedLang,
                              );
                              const level = getPersonaEnergyLevelLabel(
                                item.persona,
                              );
                              return level ? `${name} (${level})` : name;
                            })()}
                          </span>
                        </div>
                        <span className="text-muted-foreground">
                          {item.percentage.toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-base font-semibold">
                    {t("monthly.insightTitle", { defaultValue: "AI Insight ✨" })}
                  </CardTitle>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleGenerateMonthlyInsight}
                    disabled={
                      isMonthlyAiInsightLoading ||
                      !rows.length ||
                      energySeries.length === 0 ||
                      maxEnergyLevel <= 0
                    }
                    data-export-ignore="true"
                  >
                    {isMonthlyAiInsightLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {t("monthly.generateInsight", { defaultValue: "Generate" })}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isMonthlyAiInsightLoading ? (
                  <p className="text-sm text-muted-foreground">
                    {t("monthly.insightLoading", {
                      defaultValue: "Generating your monthly insight…",
                    })}
                  </p>
                ) : monthlyAiInsight ? (
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {monthlyAiInsight}
                  </p>
                ) : monthlyAiInsightError ? (
                  <p className="text-sm text-muted-foreground">
                    {monthlyAiInsightError}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t("monthly.insightClickGenerate", {
                      defaultValue:
                        "Click Generate to create your monthly AI insight.",
                    })}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
